import DownloadIcon from "@mui/icons-material/Download";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import EquipmentPublicCodeChip from "../../shared/equipmentCatalog/EquipmentPublicCodeChip";
import { toast } from "react-toastify";

import CustomerPriceOfferContractApi from "../../../../../infrastructure/api/customerPriceOfferContract";
import CustomerApplicantContractApi from "../../../../../infrastructure/api/customerApplicantContract";
import {
  CUSTOMER_OFFER_STATUS_CODES,
  normalizeCustomerPriceOfferStatus,
} from "../../../../../infrastructure/utils/pricingQuoteStatus";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import FileUploader from "../../../../components/shared/uploader/FileUploader";

const SIGN_WORKSPACE = {
  NotAvailable: "NotAvailable",
  ReadyToSign: "ReadyToSign",
  PendingSignature: "PendingSignature",
  Signed: "Signed",
  NotSignable: "NotSignable",
};

const OFFER_APPROVED_BY_SOLARENT = CUSTOMER_OFFER_STATUS_CODES.ApprovedBySolarent;
const OFFER_ACCEPTED = CUSTOMER_OFFER_STATUS_CODES.AcceptedByCustomer;
const CONTRACT_UPLOADED = 2;
const CONTRACT_APPROVED = 3;

const formatMoney = (v) =>
  v > 0 ? Number(v).toLocaleString("fa-IR") : "—";

const normalizeProjectSummary = (raw) => {
  if (!raw) return null;
  return {
    summaryLine: raw?.summaryLine ?? raw?.SummaryLine ?? "",
    plantCapacityTitle: raw?.plantCapacityTitle ?? raw?.PlantCapacityTitle ?? "",
    powerPlantTypeTitle: raw?.powerPlantTypeTitle ?? raw?.PowerPlantTypeTitle ?? "",
    contractSchemeTitle: raw?.contractSchemeTitle ?? raw?.ContractSchemeTitle ?? "",
  };
};

const normalizeOffer = (raw) => ({
  id: raw?.id ?? raw?.Id,
  requestId: raw?.requestId ?? raw?.RequestId,
  requestReferenceId: raw?.requestReferenceId ?? raw?.RequestReferenceId ?? "",
  status: normalizeCustomerPriceOfferStatus(raw?.status ?? raw?.Status),
  statusTitle: raw?.statusTitle ?? raw?.StatusTitle ?? "",
  customerPriceFinal: raw?.customerPriceFinal ?? raw?.CustomerPriceFinal ?? 0,
  canDownloadContract: raw?.canDownloadContract ?? raw?.CanDownloadContract,
  canUploadSignedContract: raw?.canUploadSignedContract ?? raw?.CanUploadSignedContract,
  applicantContractId: raw?.applicantContractId ?? raw?.ApplicantContractId ?? null,
  projectSummary: normalizeProjectSummary(raw?.projectSummary ?? raw?.ProjectSummary),
  equipmentItems: (raw?.equipmentItems ?? raw?.EquipmentItems ?? []).map((item) => ({
    equipmentTitle: item?.equipmentTitle ?? item?.EquipmentTitle ?? "—",
    brand: item?.brand ?? item?.Brand ?? "",
    model: item?.model ?? item?.Model ?? "",
    publicCode: item?.publicCode ?? item?.PublicCode ?? "",
    quantity: item?.quantity ?? item?.Quantity ?? 0,
    unit: item?.unit ?? item?.Unit ?? "عدد",
  })),
  contractAcceptance: raw?.contractAcceptance ?? raw?.ContractAcceptance,
});

const normalizeContract = (raw) => {
  if (!raw) return null;
  return {
    id: raw?.id ?? raw?.Id,
    reviewStatus: raw?.reviewStatus ?? raw?.ReviewStatus,
    reviewStatusTitle: raw?.reviewStatusTitle ?? raw?.ReviewStatusTitle ?? "",
    contractFileTemplateUrl:
      raw?.contractFileTemplateUrl ?? raw?.ContractFileTemplateUrl ?? "",
    applicantContractId: raw?.applicantContractId ?? raw?.ApplicantContractId ?? null,
    uploadedSignedContractUrl:
      raw?.uploadedSignedContractUrl ?? raw?.UploadedSignedContractUrl ?? "",
    rejectionReason: raw?.rejectionReason ?? raw?.RejectionReason ?? "",
  };
};

const normalizeSignSummary = (raw) => {
  if (!raw) return null;
  return {
    contractId: raw?.contractId ?? raw?.ContractId ?? null,
    workspaceState: raw?.workspaceState ?? raw?.WorkspaceState ?? SIGN_WORKSPACE.NotAvailable,
    canDownloadPdf: raw?.canDownloadPdf ?? raw?.CanDownloadPdf ?? false,
    canSendSignatureOtp: raw?.canSendSignatureOtp ?? raw?.CanSendSignatureOtp ?? false,
    isSigned: raw?.isSigned ?? raw?.IsSigned ?? false,
    statusTitle: raw?.statusTitle ?? raw?.StatusTitle ?? "",
    message: raw?.message ?? raw?.Message ?? "",
  };
};

const CustomerPriceOfferContractPage = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [offer, setOffer] = useState(null);
  const [signSummary, setSignSummary] = useState(null);
  const [signedFileUrl, setSignedFileUrl] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [showScanUpload, setShowScanUpload] = useState(false);

  const loadOffer = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    try {
      const res = await CustomerPriceOfferContractApi.getOfferForCustomer(requestId);
      if (!res.ok) {
        toast.error(res.message || "اطلاعات قیمت پروژه یافت نشد.");
        setOffer(null);
        setSignSummary(null);
      } else {
        const nextOffer = normalizeOffer(res.data);
        setOffer(nextOffer);
        const contract = normalizeContract(res.data?.contractAcceptance);
        if (contract?.uploadedSignedContractUrl) {
          setSignedFileUrl(contract.uploadedSignedContractUrl);
        }

        if (
          nextOffer.status === OFFER_ACCEPTED ||
          nextOffer.status === OFFER_APPROVED_BY_SOLARENT
        ) {
          const signRes = await CustomerApplicantContractApi.getSummary(requestId);
          setSignSummary(signRes.ok ? normalizeSignSummary(signRes.data) : null);
        } else {
          setSignSummary(null);
        }
      }
    } catch {
      toast.error("خطا در بارگذاری اطلاعات قیمت پروژه.");
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    loadOffer();
  }, [loadOffer]);

  const contract = normalizeContract(offer?.contractAcceptance);

  const handleDownload = async () => {
    setActionLoading(true);
    try {
      const contractId =
        signSummary?.contractId ||
        offer?.applicantContractId ||
        contract?.applicantContractId ||
        null;
      if (!contractId) {
        toast.error(
          "فایل قرارداد هنوز توسط سولارنت تولید نشده است. پس از تولید قرارداد، دوباره تلاش کنید."
        );
        return;
      }

      await CustomerPriceOfferContractApi.markContractDownloaded(requestId);
      const blob = await CustomerApplicantContractApi.downloadPdf(contractId);
      if (!blob) {
        toast.error("دانلود فایل قرارداد ناموفق بود.");
        return;
      }

      const url = window.URL.createObjectURL(
        new Blob([blob], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `applicant-contract-${requestId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      await loadOffer();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "خطا در دانلود قرارداد."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleUploadSigned = async () => {
    if (!signedFileUrl) {
      toast.error("ابتدا فایل امضا‌شده را آپلود کنید.");
      return;
    }
    setActionLoading(true);
    try {
      const res = await CustomerPriceOfferContractApi.uploadSignedCustomerContract({
        requestId: Number(requestId),
        uploadedSignedContractUrl: signedFileUrl,
      });
      if (res.ok) {
        toast.success(res.message || "قرارداد ثبت شد.");
        setOffer(normalizeOffer({ ...offer, contractAcceptance: res.data }));
        loadOffer();
      } else {
        toast.error(res.message);
      }
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (!offer) {
    return (
      <Alert severity="info">
        مبلغ نهایی پروژه برای این درخواست هنوز آماده نیست. پس از تایید توسط سولارنت، این
        بخش فعال می‌شود.
      </Alert>
    );
  }

  const summaryLine =
    offer.projectSummary?.summaryLine ||
    [
      offer.projectSummary?.powerPlantTypeTitle,
      offer.projectSummary?.plantCapacityTitle,
      offer.projectSummary?.contractSchemeTitle,
    ]
      .filter(Boolean)
      .join(" - ");

  return (
    <Grid container spacing={2} data-testid="price-offer-card">
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>
      <Grid item xs={12}>
        <Card>
          <CardHeader
            title="قیمت و قرارداد پروژه"
            subheader={
              offer.requestReferenceId
                ? `درخواست ${offer.requestReferenceId}`
                : `شناسه درخواست ${offer.requestId}`
            }
            action={
              <Button size="small" onClick={() => navigate("/SLN/Customer/Requests")}>
                بازگشت به درخواست‌ها
              </Button>
            }
          />
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                <Chip label={offer.statusTitle} color="primary" variant="outlined" />
                <Chip
                  label={`مبلغ نهایی پروژه: ${formatMoney(offer.customerPriceFinal)} ریال`}
                  color="primary"
                />
              </Stack>

              {summaryLine ? (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    مشخصات کلی پروژه
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {summaryLine}
                  </Typography>
                </Paper>
              ) : null}

              {offer.equipmentItems?.length > 0 ? (
                <>
                  <Typography variant="subtitle2" fontWeight={700}>
                    فهرست تجهیزات
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>کد</TableCell>
                        <TableCell>تجهیز</TableCell>
                        <TableCell align="center">تعداد</TableCell>
                        <TableCell align="center">واحد</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {offer.equipmentItems.map((row, idx) => (
                        <TableRow key={`${row.equipmentTitle}-${idx}`}>
                          <TableCell>
                            <EquipmentPublicCodeChip code={row.publicCode} />
                          </TableCell>
                          <TableCell>
                            {row.brand || row.model
                              ? `${row.brand} ${row.model}`.trim()
                              : row.equipmentTitle}
                          </TableCell>
                          <TableCell align="center">
                            {Number(row.quantity) > 0
                              ? Number(row.quantity).toLocaleString("fa-IR")
                              : "—"}
                          </TableCell>
                          <TableCell align="center">{row.unit || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              ) : (
                <Alert severity="info">فهرست تجهیزات برای این پروژه ثبت نشده است.</Alert>
              )}

              {offer.status === OFFER_APPROVED_BY_SOLARENT ||
              offer.status === OFFER_ACCEPTED ? (
                <Alert severity="success">
                  قیمت نهایی تایید شده است. پس از تولید قرارداد توسط سولارنت می‌توانید
                  آن را در همین پنل مشاهده و به‌صورت دیجیتال امضا کنید.
                </Alert>
              ) : null}

              <Divider />

              {offer.status === OFFER_APPROVED_BY_SOLARENT ||
              offer.status === OFFER_ACCEPTED ? (
                <Stack spacing={2}>
                  <Alert severity="success">قرارداد متقاضی آماده مشاهده و امضا است.</Alert>

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                      امضای دیجیتال قرارداد
                    </Typography>
                    {signSummary?.isSigned ||
                    signSummary?.workspaceState === SIGN_WORKSPACE.Signed ? (
                      <Alert severity="success" sx={{ mb: 1.5 }}>
                        قرارداد به‌صورت دیجیتال امضا شده است.
                      </Alert>
                    ) : signSummary?.workspaceState === SIGN_WORKSPACE.ReadyToSign ||
                      signSummary?.workspaceState === SIGN_WORKSPACE.PendingSignature ||
                      offer.canDownloadContract ||
                      signSummary?.contractId ? (
                      <Alert severity="info" sx={{ mb: 1.5 }}>
                        قرارداد آماده است. از دکمه زیر برای مشاهده PDF و امضای دیجیتال با کد
                        پیامکی استفاده کنید.
                      </Alert>
                    ) : (
                      <Alert severity="warning" sx={{ mb: 1.5 }}>
                        قرارداد هنوز توسط سولارنت تولید نشده است. به‌محض تولید، همین‌جا می‌توانید
                        آن را مشاهده و به‌صورت دیجیتال امضا کنید.
                      </Alert>
                    )}

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <Button
                        variant="contained"
                        disabled={actionLoading}
                        onClick={() =>
                          navigate(`/SLN/Customer/ApplicantContractSign/${requestId}`)
                        }
                      >
                        مشاهده و امضای دیجیتال
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        disabled={
                          actionLoading ||
                          !(
                            offer.canDownloadContract ||
                            signSummary?.canDownloadPdf ||
                            signSummary?.contractId
                          )
                        }
                        onClick={handleDownload}
                      >
                        دانلود قرارداد
                      </Button>
                    </Stack>
                  </Paper>

                  {contract ? (
                    <Alert
                      severity={
                        Number(contract.reviewStatus) === CONTRACT_APPROVED
                          ? "success"
                          : Number(contract.reviewStatus) === CONTRACT_UPLOADED
                            ? "info"
                            : "warning"
                      }
                    >
                      {Number(contract.reviewStatus) === 1
                        ? signSummary?.isSigned
                          ? "وضعیت قرارداد: امضای دیجیتال ثبت شده — در انتظار بررسی سولارنت"
                          : signSummary?.contractId
                            ? "وضعیت قرارداد: در انتظار امضای دیجیتال متقاضی"
                            : "وضعیت قرارداد: در انتظار تولید قرارداد توسط سولارنت"
                        : `وضعیت بررسی سولارنت: ${contract.reviewStatusTitle}`}
                      {contract.rejectionReason
                        ? ` — ${contract.rejectionReason}`
                        : ""}
                    </Alert>
                  ) : null}

                  {Number(contract?.reviewStatus) === CONTRACT_APPROVED ? (
                    <Stack spacing={1}>
                      <Alert severity="info">
                        پرداخت این پرونده فقط از طریق فاکتور صادرشده انجام می‌شود.
                      </Alert>
                      <Button
                        variant="contained"
                        onClick={() => navigate("/SLN/Customer")}
                      >
                        مشاهده فاکتور و پرداخت
                      </Button>
                    </Stack>
                  ) : null}

                  {offer.canUploadSignedContract ? (
                    <Box>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setShowScanUpload((v) => !v)}
                      >
                        {showScanUpload
                          ? "بستن روش جایگزین (آپلود اسکن)"
                          : "روش جایگزین: آپلود اسکن قرارداد امضا‌شده"}
                      </Button>
                      {showScanUpload ? (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            آپلود قرارداد امضا‌شده (اسکن)
                          </Typography>
                          <FileUploader
                            moduleName="CustomerContracts"
                            accept="application/pdf,image/*"
                            maxFiles={1}
                            label="فایل قرارداد امضا‌شده"
                            value={signedFileUrl}
                            onChange={(url) => setSignedFileUrl(url)}
                          />
                          <Button
                            sx={{ mt: 1 }}
                            variant="contained"
                            disabled={actionLoading || !signedFileUrl}
                            onClick={handleUploadSigned}
                          >
                            ارسال قرارداد امضا‌شده
                          </Button>
                        </Box>
                      ) : null}
                    </Box>
                  ) : null}
                </Stack>
              ) : null}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default CustomerPriceOfferContractPage;
